import {
  type AwsRdsEngine,
  type Configuration,
  type RestoreFilesRequest,
  type RestoreGoogleWorkspaceRequest,
  type RestoreMicrosoft365Request,
  type RestoreDbToRdsInstanceRequest,
  type RestoreBucketRequest,
  type RestoreAtlasClusterInput,
  type RestoreAwsEc2InstanceRequest,
  type ListAzureStorageAccountContainers200Response,
  type RestoreImageInput,
  type RestoreAzureVmInstanceRequest,
  type RestoreAzureDiskRequest,
  type RestoreAzureEntraIdRequest,
  type RestoreVolumeToEbsRequest,
  type ListAzureSKUs200Response,
  type ResourceType,
  type RestoreEksNamespaceInput,
  type RestoreAzureSqlDatabaseRequest,
  type RestoreAzurePostgresDatabaseRequest,
  type RestoreGcpDiskRequest,
  type RestoreGcpVmInstanceRequest,
  type RestoreDynamoDBTableRequest,
  type RestoreKeyspacesTableRequest,
  type RestoreAwsCassandraClusterRequest,
  type RestoreGcpCloudSqlRequest,
  type RestoreBigQueryDatasetRequest,
  type RestoreFSxRequest,
  type RestoreEFSRequest,
  type RestoreDocumentDBRequest,
  type RestoreNeptuneRequest,
  ListNeptuneClusterParameterGroupsApi,
  type RestoreRdsNativePitrRequest,
  type RestoreAccount,
  type RestoreBigQueryNativePitrRequest,
  type RestoreAzureSqlDatabaseNativePitrRequest,
  type RestoreAzureSqlVmPitrRequest,
  type RestoreAzureFileShareRequest,
  type ExportAccessDeniedItemsRequest,
  type BulkRestoreRequest,
  type BulkRestoreValidateRequest,
  type BulkSelectSnapshotsForResourcesRequest,
  type GetBulkRestoreSnapshotHistogramRequest,
  type InventoryResource,
} from '@repo/api-gw-sdk';
import {
  SnapshotsApi,
  ListS3BucketsApi,
  ListAvailabilityZonesApi,
  ListEncryptionKeysApi,
  ListRestoreRegionsApi,
  ListAwsSecurityGroupsApi,
  ListAwsRdsInstanceClassesApi,
  ListAwsRdsSubnetGroupsApi,
  ListAtlasProjectsApi,
  ListAtlasClustersApi,
  RestoreAtlasClusterApi,
  ListAwsInstanceTypesApi,
  ListAwsSubnetsV2Api,
  ListAwsVpcsApi,
  ListAzureStorageAccountsApi,
  ListAzureStorageAccountContainersApi,
  GetAzureQueryExportStorageApi,
  RestoreImageApi,
  ListAzureResourceGroupsApi,
  ListAzureUnassignedNetworkInterfacesApi,
  ListAzureSKUsApi,
  RestoreEksNamespaceApi,
  ListGCSBucketsApi,
  ListAzureRegionsApi,
  ListGcpEncryptionKeysApi,
  CheckAzureVMNameExistsApi,
  ListGcpMachineTypesApi,
  Provider,
  ListGcpSubnetsApi,
  ListGcpSharedSubnetsApi,
  ListGcpNetworksApi,
  ListGcpDiskTypesApi,
  ListGcpSharedNetworksApi,
  ListDocumentdbClusterParameterGroupsApi,
  ListAzureSubnetsApi,
  ListAzureVirtualNetworksApi,
  BulkRestoreApi,
} from '@repo/api-gw-sdk';

import { getAccountProvider } from '@/utils/accounts';

import { isForbiddenSwrError, type HttpClient } from './httpClient';

const baseUrl = '/restore';

type BulkRestoreValidateItem = NonNullable<
  BulkRestoreValidateRequest['items']
>[number];
type BulkRestoreValidateItemWithResource = Omit<
  BulkRestoreValidateItem,
  'resourceId'
> & {
  resource: InventoryResource;
};
type BulkRestoreValidateRequestWithResources = Omit<
  BulkRestoreValidateRequest,
  'items'
> & {
  items: BulkRestoreValidateItemWithResource[];
};

export const restore = (
  httpClient: HttpClient,
  config: Configuration,
  currentProjectId: string
) => {
  const listS3BucketsApi = new ListS3BucketsApi(config);
  const snapshotsApi = new SnapshotsApi(config);
  const listEncryptionKeysApi = new ListEncryptionKeysApi(config);
  const listAwsSecurityGroupsApi = new ListAwsSecurityGroupsApi(config);
  const listAwsRdsInstanceClassesApi = new ListAwsRdsInstanceClassesApi(config);
  const listRestoreRegionsApi = new ListRestoreRegionsApi(config);
  const listAvailabilityZonesApi = new ListAvailabilityZonesApi(config);
  const listAtlasProjectsApi = new ListAtlasProjectsApi(config);
  const listAtlasClustersApi = new ListAtlasClustersApi(config);
  const restoreAtlasClusterApi = new RestoreAtlasClusterApi(config);
  const listAwsRdsSubnetGroupsApi = new ListAwsRdsSubnetGroupsApi(config);
  const restoreImageApi = new RestoreImageApi(config);
  const listAwsInstanceTypesApi = new ListAwsInstanceTypesApi(config);
  const listAwsSubnetsV2Api = new ListAwsSubnetsV2Api(config);
  const listAwsVpcsApi = new ListAwsVpcsApi(config);
  const listAzureResourceGroupsApi = new ListAzureResourceGroupsApi(config);
  const listAzureNetworkInterfacesApi =
    new ListAzureUnassignedNetworkInterfacesApi(config);
  const listAzureVirtualNetworksApi = new ListAzureVirtualNetworksApi(config);
  const listAzureSubnetsApi = new ListAzureSubnetsApi(config);
  const listAzureStorageAccountsApi = new ListAzureStorageAccountsApi(config);
  const listAzureStorageAccountContainersApi =
    new ListAzureStorageAccountContainersApi(config);
  const getAzureQueryExportStorageApi = new GetAzureQueryExportStorageApi(
    config
  );
  const listAzureSKUSApi = new ListAzureSKUsApi(config);
  const restoreEksNamespaceApi = new RestoreEksNamespaceApi(config);
  const listGCSBucketsApi = new ListGCSBucketsApi(config);
  const listAzureRegionsApi = new ListAzureRegionsApi(config);
  const checkAzureVMNameExistsApi = new CheckAzureVMNameExistsApi(config);
  const listGcpMachineTypesApi = new ListGcpMachineTypesApi(config);
  const listGcpEncryptionKeysApi = new ListGcpEncryptionKeysApi(config);
  const listGcpSubnetsApi = new ListGcpSubnetsApi(config);
  const listGcpSharedSubnetsApi = new ListGcpSharedSubnetsApi(config);
  const listGcpDiskTypesApi = new ListGcpDiskTypesApi(config);
  const listGcpNetworksApi = new ListGcpNetworksApi(config);
  const listGcpSharedNetworksApi = new ListGcpSharedNetworksApi(config);
  const listDocumentdbParameterGroupsApi =
    new ListDocumentdbClusterParameterGroupsApi(config);
  const listNeptuneParameterGroupsApi =
    new ListNeptuneClusterParameterGroupsApi(config);
  const bulkRestoreApi = new BulkRestoreApi(config);

  return {
    bulkRestore: {
      validate: ({ items, ...req }: BulkRestoreValidateRequestWithResources) =>
        bulkRestoreApi.bulkRestoreValidate(currentProjectId, {
          ...req,
          items: items.map(({ resource, ...item }) => ({
            ...item,
            resourceId: resource.id,
          })),
        }),
      initiate: (req: BulkRestoreRequest) =>
        bulkRestoreApi.bulkRestore(currentProjectId, req),
      selectSnapshots: (req: BulkSelectSnapshotsForResourcesRequest) =>
        bulkRestoreApi.bulkSelectSnapshotsForResources(currentProjectId, req),
      useHistogram: (req: GetBulkRestoreSnapshotHistogramRequest | null) =>
        httpClient.execute(
          (
            ...args: Parameters<
              typeof bulkRestoreApi.getBulkRestoreSnapshotHistogram
            >
          ) =>
            bulkRestoreApi
              .getBulkRestoreSnapshotHistogram(...args)
              .catch(() => undefined),
          req ? baseUrl : null,
          'bulk-restore-snapshot-histogram',
          currentProjectId,
          req ?? { resourceIds: [], _from: new Date(0), to: new Date(0) }
        ),
    },
    volume: (
      resourceId: string,
      snapshotId: string,
      payload: RestoreVolumeToEbsRequest
    ) =>
      snapshotsApi.restoreEbsVolume(
        currentProjectId,
        resourceId,
        snapshotId,
        payload
      ),
    files: (
      resourceId: string,
      snapshotId: string,
      restoreFilesInput: RestoreFilesRequest
    ) =>
      snapshotsApi.restoreFiles(
        currentProjectId,
        resourceId,
        snapshotId,
        restoreFilesInput
      ),
    googleWorkspace: {
      restore: (
        resourceId: string,
        snapshotId: string,
        payload: RestoreGoogleWorkspaceRequest
      ) =>
        snapshotsApi.restoreGoogleWorkspace(
          currentProjectId,
          resourceId,
          snapshotId,
          payload
        ),
    },
    microsoft365: {
      restore: (
        resourceId: string,
        snapshotId: string,
        payload: RestoreMicrosoft365Request
      ) =>
        snapshotsApi.restoreMicrosoft365(
          currentProjectId,
          resourceId,
          snapshotId,
          payload
        ),
    },
    database: (
      id: string,
      snapshotId: string,
      restoreDatabaseInput: RestoreDbToRdsInstanceRequest
    ) =>
      snapshotsApi.restoreDatabase(
        currentProjectId,
        id,
        snapshotId,
        restoreDatabaseInput
      ),
    rdsNativePitr: (
      resourceId: string,
      restoreRdsNativePitrInput: RestoreRdsNativePitrRequest
    ) =>
      snapshotsApi.restoreRdsNativePitr(
        currentProjectId,
        resourceId,
        restoreRdsNativePitrInput
      ),
    dynamoDB: (
      id: string,
      snapshotId: string,
      restoreDynamoDBInput: RestoreDynamoDBTableRequest
    ) =>
      snapshotsApi.restoreDynamoDBTable(
        currentProjectId,
        id,
        snapshotId,
        restoreDynamoDBInput
      ),
    keyspacesTable: (
      id: string,
      snapshotId: string,
      restoreKeyspacesInput: RestoreKeyspacesTableRequest
    ) =>
      snapshotsApi.restoreKeyspacesTable(
        currentProjectId,
        id,
        snapshotId,
        restoreKeyspacesInput
      ),
    cassandraCluster: (
      id: string,
      snapshotId: string,
      restoreCassandraClusterInput: RestoreAwsCassandraClusterRequest
    ) =>
      snapshotsApi.restoreCassandraCluster(
        currentProjectId,
        id,
        snapshotId,
        restoreCassandraClusterInput
      ),
    encryptionKeys: {
      useList: (
        cloudAccountId: string | undefined,
        regionName: string | undefined,
        includeAwsManagedKeys?: boolean
      ) =>
        httpClient.execute(
          listEncryptionKeysApi.listEncryptionKeys.bind(listEncryptionKeysApi),
          cloudAccountId && regionName ? baseUrl : null,
          '/encryptionKeys',
          currentProjectId,
          cloudAccountId || '',
          regionName || '',
          includeAwsManagedKeys
        ),
    },

    regions: {
      useList: (cloudAccountId: string) =>
        httpClient.execute(
          listRestoreRegionsApi.listRestoreRegions.bind(listRestoreRegionsApi),
          baseUrl,
          '/regions',
          currentProjectId,
          cloudAccountId
        ),
      useListMany: (cloudAccounts: RestoreAccount[]) =>
        httpClient.execute(
          (projectId: string, cloudAccounts: RestoreAccount[]) =>
            Promise.all(
              cloudAccounts.map((cloudAccount) => {
                const accountProvider = getAccountProvider(cloudAccount);
                if (
                  accountProvider === Provider.Aws ||
                  accountProvider === Provider.Gcp
                ) {
                  return listRestoreRegionsApi.listRestoreRegions(
                    projectId,
                    cloudAccount.id
                  );
                } else if (accountProvider === Provider.Azure) {
                  return listAzureRegionsApi.listAzureRegions(projectId);
                } else {
                  return Promise.resolve({ regions: [] });
                }
              })
            ).then((results) => {
              const regions = new Set<string>();
              results.forEach((result) =>
                result.regions.forEach((region) => regions.add(region))
              );

              return {
                regions: Array.from(regions),
              };
            }),
          baseUrl,
          '/regions',
          currentProjectId,
          cloudAccounts
        ),
    },
    availabilityZones: {
      useList: (cloudAccountId: string, region?: string) =>
        httpClient.execute(
          listAvailabilityZonesApi.listAvailabilityZones.bind(
            listAvailabilityZonesApi
          ),
          region ? baseUrl : null,
          '/regions/availabilityZones',
          currentProjectId,
          cloudAccountId,
          region || ''
        ),
    },
    s3Buckets: {
      useList: (accountId?: string, cloudProvider?: Provider) =>
        httpClient.execute(
          listS3BucketsApi.listS3Buckets.bind(listS3BucketsApi),
          accountId && cloudProvider === Provider.Aws ? baseUrl : null,
          '/buckets',
          currentProjectId,
          accountId || ''
        ),
      restore: (
        resourceId: string,
        snapshotId: string,
        restoreBucketInput: RestoreBucketRequest
      ) =>
        snapshotsApi.restoreBucket(
          currentProjectId,
          resourceId,
          snapshotId,
          restoreBucketInput
        ),
    },
    gcsBuckets: {
      useList: (accountId?: string, cloudProvider?: Provider) =>
        httpClient.execute(
          listGCSBucketsApi.listGCSBuckets.bind(listGCSBucketsApi),
          accountId && cloudProvider === Provider.Gcp ? baseUrl : null,
          '/gcs-buckets',
          currentProjectId,
          accountId || ''
        ),
    },
    gcp: {
      useListNetworks: (accountId: string) =>
        httpClient.execute(
          listGcpNetworksApi.listGcpNetworks.bind(listGcpNetworksApi),
          baseUrl,
          '/gcp-networks',
          currentProjectId,
          accountId
        ),
      useListSharedNetworks: (accountId: string) =>
        httpClient.execute(
          listGcpSharedNetworksApi.listGcpSharedNetworks.bind(
            listGcpSharedNetworksApi
          ),
          baseUrl,
          '/gcp-shared-networks',
          currentProjectId,
          accountId
        ),
      useListSubnets: (accountId: string, region: string, network?: string) =>
        httpClient.execute(
          listGcpSubnetsApi.listGcpSubnets.bind(listGcpSubnetsApi),
          baseUrl,
          '/gcp-subnets',
          currentProjectId,
          accountId,
          region,
          network
        ),
      useListSharedSubnets: (
        accountId: string,
        region: string,
        network?: string
      ) =>
        httpClient.execute(
          listGcpSharedSubnetsApi.listGcpSharedSubnets.bind(
            listGcpSharedSubnetsApi
          ),
          baseUrl,
          '/gcp-shared-subnets',
          currentProjectId,
          accountId,
          region,
          network
        ),
      useListDiskTypes: (
        accountId: string,
        zone: string,
        machineType?: string
      ) =>
        httpClient.execute(
          listGcpDiskTypesApi.listGcpDiskTypes.bind(listGcpDiskTypesApi),
          baseUrl,
          '/gcp-disk-types',
          currentProjectId,
          accountId,
          zone,
          machineType
        ),
    },
    azureRestoreConfig: {
      useListRegions: () =>
        httpClient.execute(
          listAzureRegionsApi.listAzureRegions.bind(listAzureRegionsApi),
          baseUrl,
          '/azure-regions',
          currentProjectId
        ),
      useListResourceGroups: (accountId: string, includeAll?: boolean) =>
        httpClient.execute(
          listAzureResourceGroupsApi.listAzureResourceGroups.bind(
            listAzureResourceGroupsApi
          ),
          baseUrl,
          '/restore-resource-groups',
          currentProjectId,
          accountId,
          includeAll
        ),
      useListNetworkInterfaces: (accountId: string, resourceGroup: string) =>
        httpClient.execute(
          listAzureNetworkInterfacesApi.listAzureUnassignedNetworkInterfaces.bind(
            listAzureNetworkInterfacesApi
          ),
          baseUrl,
          '/networkInterfaces',
          currentProjectId,
          accountId,
          { resourceGroup: resourceGroup }
        ),
      useListVirtualNetworks: (
        accountId: string,
        resourceGroup: string,
        location?: string
      ) =>
        httpClient.execute(
          listAzureVirtualNetworksApi.listAzureVirtualNetworks.bind(
            listAzureVirtualNetworksApi
          ),
          baseUrl,
          '/azure/virtual-networks',
          currentProjectId,
          accountId,
          { resourceGroup, location }
        ),
      useListSubnets: (
        accountId: string,
        resourceGroup: string,
        virtualNetworkName: string
      ) =>
        httpClient.execute(
          (...args: Parameters<typeof listAzureSubnetsApi.listAzureSubnets>) =>
            !virtualNetworkName
              ? Promise.resolve({ subnets: [] })
              : listAzureSubnetsApi.listAzureSubnets(...args),
          baseUrl,
          '/azure/subnets',
          currentProjectId,
          accountId,
          { resourceGroup, virtualNetworkName }
        ),
      useListSKUs: (
        accountId: string,
        location: string,
        resourceType:
          | ResourceType.AzureVirtualMachine
          | ResourceType.AzureSqlVirtualMachine
          | ResourceType.AzureSapHanaVm
          | ResourceType.AzureDisk
          | ResourceType.AzureSqlDatabase
      ) =>
        httpClient.execute(
          (...args: Parameters<typeof listAzureSKUSApi.listAzureSKUs>) =>
            location
              ? listAzureSKUSApi.listAzureSKUs(...args)
              : Promise.resolve({ skus: [] } as ListAzureSKUs200Response),

          baseUrl,
          '/SKUS',
          currentProjectId,
          accountId,
          {
            location,
            resourceType,
          }
        ),
    },
    queryExportStorage: {
      useGet: (accountId: string) =>
        httpClient.executeWithConfig(
          getAzureQueryExportStorageApi.getAzureQueryExportStorage.bind(
            getAzureQueryExportStorageApi
          ),
          accountId ? baseUrl : null,
          '/queryExportStorage',
          {
            onError: (err) => {
              // 403 means the restore subscription's management role is missing the
              // storage-account permission this lazily creates - a customer-side
              // onboarding gap, not an app bug, so it must not be reported as a crash.
              if (isForbiddenSwrError(err)) {
                return;
              }
              window.reportError(err);
            },
          },
          currentProjectId,
          accountId
        ),
    },
    storageAccounts: {
      useList: (accountId: string) =>
        httpClient.execute(
          listAzureStorageAccountsApi.listAzureStorageAccounts.bind(
            listAzureStorageAccountsApi
          ),
          baseUrl,
          '/storageAccounts',
          currentProjectId,
          accountId
        ),
      useListContainers: (
        accountId: string,
        storageAccountName: string,
        resourceGroup: string
      ) =>
        httpClient.execute(
          (
            ...args: Parameters<
              typeof listAzureStorageAccountContainersApi.listAzureStorageAccountContainers
            >
          ) =>
            !storageAccountName || !resourceGroup
              ? Promise.resolve({
                  containers: [],
                } as ListAzureStorageAccountContainers200Response)
              : listAzureStorageAccountContainersApi.listAzureStorageAccountContainers(
                  ...args
                ),
          baseUrl,
          '/storageAccounts/containers',
          currentProjectId,
          accountId,
          {
            storageAccount: {
              name: storageAccountName,
              resourceGroup: resourceGroup,
              location: '',
            },
          }
        ),
    },
    securityGroups: {
      useList: (
        cloudAccountId: string,
        region?: string,
        vpc?: string,
        subnetId?: string
      ) =>
        httpClient.execute(
          listAwsSecurityGroupsApi.listAwsSecurityGroups.bind(
            listAwsSecurityGroupsApi
          ),
          region ? baseUrl : null,
          '/regions/aws-security-groups',
          currentProjectId,
          cloudAccountId,
          region || '',
          vpc,
          subnetId
        ),
    },
    rdsInstanceClasses: {
      useList: (
        cloudAccountId: string,
        engine: AwsRdsEngine,
        engineVersion: string,
        region: string
      ) =>
        httpClient.execute(
          (
            ...args: Parameters<
              typeof listAwsRdsInstanceClassesApi.listAwsRdsInstanceClasses
            >
          ) => listAwsRdsInstanceClassesApi.listAwsRdsInstanceClasses(...args),
          baseUrl,
          '/regions/aws-rds-instance-classes',
          currentProjectId,
          engine,
          engineVersion,
          cloudAccountId,
          region
        ),
    },
    rdsSubnetGroup: {
      useList: (cloudAccountId: string, region: string) =>
        httpClient.execute(
          listAwsRdsSubnetGroupsApi.listAwsRdsSubnetGroups.bind(
            listAwsRdsSubnetGroupsApi
          ),
          region ? baseUrl : null,
          '/regions/aws-rds-subnet-groups',
          currentProjectId,
          cloudAccountId,
          region
        ),
    },
    atlasProjects: {
      useList: (accountId: string) =>
        httpClient.execute(
          listAtlasProjectsApi.listAtlasProjects.bind(listAtlasProjectsApi),
          baseUrl,
          '/atlasProjects',
          currentProjectId,
          accountId
        ),
      useListClusters: (accountId: string) =>
        httpClient.execute(
          listAtlasClustersApi.listAtlasClusters.bind(listAtlasClustersApi),
          baseUrl,
          '/atlasClusters',
          currentProjectId,
          accountId
        ),
      restore: (
        resourceId: string,
        snapshotId: string,
        restoreAtlasClusterInput: RestoreAtlasClusterInput
      ) =>
        restoreAtlasClusterApi.restoreAtlasCluster(
          currentProjectId,
          resourceId,
          snapshotId,
          restoreAtlasClusterInput
        ),
    },
    instanceTypes: {
      useList: (cloudAccountId: string, region: string, subnetId: string) =>
        httpClient.execute(
          (
            ...args: Parameters<
              typeof listAwsInstanceTypesApi.listAwsInstanceTypes
            >
          ) =>
            listAwsInstanceTypesApi.listAwsInstanceTypes(...args).catch((x) => {
              if (x.code !== 404) {
                throw x;
              }
            }),
          baseUrl,
          '/listAwsInstanceTypes',
          currentProjectId,
          cloudAccountId,
          region,
          subnetId
        ),
    },
    awsSubnets: {
      useList: (cloudAccountId: string, region?: string) =>
        httpClient.execute(
          listAwsSubnetsV2Api.listAwsSubnetsV2.bind(listAwsSubnetsV2Api),
          region ? baseUrl : null,
          '/listAwsSubnets',
          currentProjectId,
          cloudAccountId,
          region || ''
        ),
    },
    awsVpcs: {
      useList: (cloudAccountId: string, region?: string) =>
        httpClient.execute(
          listAwsVpcsApi.listAwsVpcs.bind(listAwsVpcsApi),
          region ? baseUrl : null,
          '/listAwsVpcs',
          currentProjectId,
          cloudAccountId,
          region || ''
        ),
    },
    ec2: {
      restore: (
        resourceId: string,
        snapshotId: string,
        payload: RestoreAwsEc2InstanceRequest
      ) =>
        snapshotsApi.restoreEc2Instance(
          currentProjectId,
          resourceId,
          snapshotId,
          payload
        ),
      restoreImage: (
        resourceId: string,
        snapshotId: string,
        payload: RestoreImageInput
      ) =>
        restoreImageApi.restoreImage(
          currentProjectId,
          resourceId,
          snapshotId,
          payload
        ),
    },
    azureVm: {
      useCheckNameExists: (
        cloudAccountId?: string,
        resourceGroup?: string,
        vmName?: string
      ) =>
        httpClient.executeWithConfig(
          checkAzureVMNameExistsApi.checkAzureVMNameExists.bind(
            checkAzureVMNameExistsApi
          ),
          cloudAccountId && resourceGroup && vmName ? baseUrl : null,
          '/azure/vm-name-exists',
          {
            keepPreviousData: false,
          },
          currentProjectId,
          {
            cloudAccountId: cloudAccountId || '',
            resourceGroup: resourceGroup || '',
            vmName: vmName || '',
          }
        ),
      restore: (
        resourceId: string,
        snapshotId: string,
        payload: RestoreAzureVmInstanceRequest
      ) =>
        snapshotsApi.restoreAzureVmInstance(
          currentProjectId,
          resourceId,
          snapshotId,
          payload
        ),
      restoreDisk: (
        resourceId: string,
        snapshotId: string,
        payload: RestoreAzureDiskRequest
      ) =>
        snapshotsApi.restoreAzureDisk(
          currentProjectId,
          resourceId,
          snapshotId,
          payload
        ),
    },
    azureEntraId: {
      restore: (
        resourceId: string,
        snapshotId: string,
        payload: RestoreAzureEntraIdRequest
      ) =>
        snapshotsApi.restoreAzureEntraId(
          currentProjectId,
          resourceId,
          snapshotId,
          payload
        ),
    },
    azureSqlVm: {
      pitrRestore: (
        resourceId: string,
        payload: RestoreAzureSqlVmPitrRequest
      ) =>
        snapshotsApi.restoreAzureSqlVmPitr(
          currentProjectId,
          resourceId,
          payload
        ),
    },
    azureFileShare: {
      restore: (resourceId: string, payload: RestoreAzureFileShareRequest) =>
        snapshotsApi.restoreAzureFileShare(
          currentProjectId,
          resourceId,
          payload
        ),
    },
    azureMssql: {
      restore: (
        resourceId: string,
        snapshotId: string,
        payload: RestoreAzureSqlDatabaseRequest
      ) =>
        snapshotsApi.restoreAzureSqlDatabase(
          currentProjectId,
          resourceId,
          snapshotId,
          payload
        ),
      nativePitr: (
        resourceId: string,
        payload: RestoreAzureSqlDatabaseNativePitrRequest
      ) =>
        snapshotsApi.restoreAzureSqlDatabaseNativePitr(
          currentProjectId,
          resourceId,
          payload
        ),
    },
    azurePostgres: {
      restore: (
        resourceId: string,
        snapshotId: string,
        payload: RestoreAzurePostgresDatabaseRequest
      ) =>
        snapshotsApi.restoreAzurePostgresDatabase(
          currentProjectId,
          resourceId,
          snapshotId,
          payload
        ),
    },
    eks: {
      restoreNamespace: (
        resourceId: string,
        snapshotId: string,
        payload: RestoreEksNamespaceInput
      ) =>
        restoreEksNamespaceApi.restoreEksNamespace(
          currentProjectId,
          resourceId,
          snapshotId,
          payload
        ),
    },
    gcpVm: {
      restoreDisk: (
        resourceId: string,
        snapshotId: string,
        payload: RestoreGcpDiskRequest
      ) =>
        snapshotsApi.restoreGcpDisk(
          currentProjectId,
          resourceId,
          snapshotId,
          payload
        ),
      restoreVm: (
        resourceId: string,
        snapshotId: string,
        payload: RestoreGcpVmInstanceRequest
      ) =>
        snapshotsApi.restoreGcpVmInstance(
          currentProjectId,
          resourceId,
          snapshotId,
          payload
        ),
      useListMachineTypes: (accountId: string, zone: string) =>
        httpClient.execute(
          listGcpMachineTypesApi.listGcpMachineTypes.bind(
            listGcpMachineTypesApi
          ),
          baseUrl,
          '/listGcpMachineTypes',
          currentProjectId,
          accountId,
          zone
        ),
      useListEncryptionKeys: (
        accountId: string | undefined,
        region: string | undefined
      ) =>
        httpClient.execute(
          listGcpEncryptionKeysApi.listGcpEncryptionKeys.bind(
            listGcpEncryptionKeysApi
          ),
          accountId && region ? baseUrl : null,
          '/listGcpEncryptionKeys',
          currentProjectId,
          accountId || '',
          region || ''
        ),
    },
    gcpCloudSql: {
      restore: (
        resourceId: string,
        snapshotId: string,
        payload: RestoreGcpCloudSqlRequest
      ) =>
        snapshotsApi.restoreGcpCloudSql(
          currentProjectId,
          resourceId,
          snapshotId,
          payload
        ),
    },
    bigQuery: {
      restore: (
        resourceId: string,
        snapshotId: string,
        payload: RestoreBigQueryDatasetRequest
      ) =>
        snapshotsApi.restoreBigQueryDataset(
          currentProjectId,
          resourceId,
          snapshotId,
          payload
        ),
      nativePitrRestore: (
        resourceId: string,
        payload: RestoreBigQueryNativePitrRequest
      ) =>
        snapshotsApi.restoreBigQueryNativePitr(
          currentProjectId,
          resourceId,
          payload
        ),
    },
    fsx: {
      restore: (
        resourceId: string,
        snapshotId: string,
        payload: RestoreFSxRequest
      ) =>
        snapshotsApi.restoreFSx(
          currentProjectId,
          resourceId,
          snapshotId,
          payload
        ),
    },
    efs: {
      restore: (
        resourceId: string,
        snapshotId: string,
        payload: RestoreEFSRequest
      ) =>
        snapshotsApi.restoreEFS(
          currentProjectId,
          resourceId,
          snapshotId,
          payload
        ),
    },
    documentdb: {
      restore: (
        resourceId: string,
        snapshotId: string,
        payload: RestoreDocumentDBRequest
      ) =>
        snapshotsApi.restoreDocumentdb(
          currentProjectId,
          resourceId,
          snapshotId,
          payload
        ),
    },
    neptune: {
      restore: (
        resourceId: string,
        snapshotId: string,
        payload: RestoreNeptuneRequest
      ) =>
        snapshotsApi.restoreNeptune(
          currentProjectId,
          resourceId,
          snapshotId,
          payload
        ),
    },
    neptuneParameterGroups: {
      useList: (accountId?: string, region?: string) =>
        httpClient.execute(
          listNeptuneParameterGroupsApi.listNeptuneClusterParameterGroups.bind(
            listNeptuneParameterGroupsApi
          ),
          accountId && region ? baseUrl : null,
          '/regions/aws-neptune-parameter-groups',
          currentProjectId,
          accountId || '',
          region || ''
        ),
    },
    exportAccessDenied: (
      resourceId: string,
      snapshotId: string,
      payload: ExportAccessDeniedItemsRequest
    ) =>
      snapshotsApi.exportAccessDeniedItems(
        currentProjectId,
        resourceId,
        snapshotId,
        payload
      ),
    documentdbParameterGroups: {
      useList: (accountId?: string, region?: string) =>
        httpClient.execute(
          listDocumentdbParameterGroupsApi.listDocumentdbClusterParameterGroups.bind(
            listDocumentdbParameterGroupsApi
          ),
          accountId && region ? baseUrl : null,
          '/regions/aws-documentdb-parameter-groups',
          currentProjectId,
          accountId || '',
          region || ''
        ),
    },
  };
};
